import type { TFunction } from "i18next";
import {
  CONTENT_LANGUAGE_AUTO,
  CONTENT_LANGUAGE_OPTIONS,
} from "../../../../i18n/contentLanguages";

export interface VoiceCatalogueFilterOption {
  label: string;
  value: string;
}

export interface VoiceCatalogueTranslatedFilterOption {
  labelKey: string;
  value: string;
}

export const VOICE_CATALOGUE_GENDER_OPTIONS: readonly VoiceCatalogueTranslatedFilterOption[] = [
  { labelKey: "voiceTabContent.filterGenderAny", value: "" },
  { labelKey: "voiceTabContent.filterGenderFemale", value: "female" },
  { labelKey: "voiceTabContent.filterGenderMale", value: "male" },
];

export const VOICE_CATALOGUE_AGE_OPTIONS: readonly VoiceCatalogueTranslatedFilterOption[] = [
  { labelKey: "voiceTabContent.filterAgeAny", value: "" },
  { labelKey: "voiceTabContent.filterAgeYoung", value: "young" },
  { labelKey: "voiceTabContent.filterAgeMiddleAged", value: "middle_aged" },
  { labelKey: "voiceTabContent.filterAgeOld", value: "old" },
];

// Values come from ElevenLabs GET /v1/voices/accents for the Dreamwrite
// content-language set. The value is the exact provider accent filter.
export const ELEVENLABS_ACCENT_OPTIONS_BY_LANGUAGE: Record<
  string,
  readonly VoiceCatalogueFilterOption[]
> = {
  en: [
    { value: "american", label: "American" },
    { value: "australian", label: "Australian" },
    { value: "british", label: "British" },
    { value: "canadian", label: "Canadian" },
    { value: "indian", label: "Indian" },
    { value: "irish", label: "Irish" },
    { value: "new zealand", label: "New Zealand" }, // i18n-exempt: provider-defined ElevenLabs accent name.
    { value: "nigerian", label: "Nigerian" },
    { value: "scottish", label: "Scottish" },
    { value: "south african", label: "South African" }, // i18n-exempt: provider-defined ElevenLabs accent name.
    { value: "african american", label: "African American" }, // i18n-exempt: provider-defined ElevenLabs accent name.
    { value: "singaporean", label: "Singaporean" },
    { value: "boston", label: "US - Boston" }, // i18n-exempt: provider-defined ElevenLabs accent name.
    { value: "new york", label: "US - New York" }, // i18n-exempt: provider-defined ElevenLabs accent name.
    { value: "us southern", label: "US - Southern" }, // i18n-exempt: provider-defined ElevenLabs accent name.
    { value: "us midwest", label: "US - Midwest" }, // i18n-exempt: provider-defined ElevenLabs accent name.
    { value: "us northeast", label: "US - Northeast" }, // i18n-exempt: provider-defined ElevenLabs accent name.
    { value: "received pronunciation", label: "Received Pronunciation" }, // i18n-exempt: provider-defined ElevenLabs accent name.
    { value: "yorkshire", label: "Yorkshire" },
  ],
  ja: [
    { value: "kansai", label: "Kansai" },
    { value: "kanto", label: "Kanto" },
    { value: "standard", label: "Standard" },
  ],
  zh: [
    { value: "beijing mandarin", label: "Mandarin (Beijing)" },
    { value: "taiwan mandarin", label: "Mandarin (Taiwan)" },
    { value: "standard", label: "Standard" },
  ],
  de: [
    { value: "bavarian", label: "Bavarian" },
    { value: "standard", label: "Standard" },
  ],
  hi: [
    { value: "bihari", label: "Bihari" },
    { value: "standard", label: "Standard" },
  ],
  fr: [
    { value: "african", label: "African" },
    { value: "parisian", label: "Parisian" },
    { value: "quebec", label: "Quebec" },
    { value: "standard", label: "Standard" },
  ],
  ko: [
    { value: "seoul", label: "Seoul" },
    { value: "gyeongsang", label: "Gyeongsang" },
    { value: "standard", label: "Standard" },
  ],
  pt: [
    { value: "brazilian", label: "Brazilian" },
    { value: "european", label: "European" },
  ],
  it: [
    { value: "milanese", label: "Milanese" },
    { value: "sicilian", label: "Sicilian" },
    { value: "standard", label: "Standard" },
  ],
  es: [
    { value: "latin american", label: "Latin American" }, // i18n-exempt: provider-defined ElevenLabs accent name.
    { value: "peninsular", label: "Peninsular" },
    { value: "andalusian", label: "Andalusian" },
    { value: "argentine", label: "Argentine" },
    { value: "chilean", label: "Chilean" },
    { value: "colombian", label: "Colombian" },
    { value: "mexican", label: "Mexican" },
    { value: "peruvian", label: "Peruvian" },
    { value: "venezuelan", label: "Venezuelan" },
  ],
  id: [
    { value: "javanese", label: "Javanese" },
    { value: "standard", label: "Standard" },
  ],
  nl: [
    { value: "flemish", label: "Flemish" },
    { value: "standard", label: "Standard" },
  ],
  tr: [
    { value: "central", label: "Central" },
    { value: "istanbul", label: "Istanbul" },
    { value: "standard", label: "Standard" },
  ],
  fil: [{ value: "standard", label: "Standard" }],
  pl: [
    { value: "mazovian", label: "Mazovian" },
    { value: "standard", label: "Standard" },
  ],
  sv: [
    { value: "standard", label: "Standard" },
    { value: "stockholm", label: "Stockholm" },
  ],
  bg: [{ value: "standard", label: "Standard" }],
  ro: [{ value: "standard", label: "Standard" }],
  ar: [
    { value: "modern standard", label: "Modern Standard" }, // i18n-exempt: provider-defined ElevenLabs accent name.
    { value: "egyptian", label: "Egyptian" },
    { value: "gulf", label: "Gulf" },
    { value: "levantine", label: "Levantine" },
    { value: "saudi", label: "Saudi" },
  ],
  cs: [
    { value: "moravian", label: "Moravian" },
    { value: "prague", label: "Prague" },
    { value: "standard", label: "Standard" },
  ],
  el: [{ value: "standard", label: "Standard" }],
  fi: [{ value: "standard", label: "Standard" }],
  hr: [{ value: "standard", label: "Standard" }],
  ms: [
    { value: "malaysian", label: "Malaysian" },
    { value: "standard", label: "Standard" },
  ],
  sk: [
    { value: "central", label: "Central" },
    { value: "standard", label: "Standard" },
    { value: "western", label: "Western" },
  ],
  da: [
    { value: "copenhagen", label: "Copenhagen" },
    { value: "standard", label: "Standard" },
  ],
  ta: [
    { value: "chennai", label: "Chennai" },
    { value: "standard", label: "Standard" },
  ],
  uk: [
    { value: "kiev", label: "Kyiv" },
    { value: "standard", label: "Standard" },
  ],
  ru: [
    { value: "moscow", label: "Moscow" },
    { value: "standard", label: "Standard" },
  ],
  vi: [
    { value: "northern", label: "Northern" },
    { value: "central", label: "Central" },
    { value: "southern", label: "Southern" },
    { value: "standard", label: "Standard" },
  ],
};

export function buildVoiceCatalogueLanguageOptions(
  t: TFunction,
): VoiceCatalogueFilterOption[] {
  return [
    { label: t("voiceTabContent.filterLanguageAny"), value: "" },
    ...CONTENT_LANGUAGE_OPTIONS.filter(
      (option) => option.value !== CONTENT_LANGUAGE_AUTO,
    ).map((option) => {
      const endonym = option.label ?? option.value;
      const name = t("shared:languageNames." + option.value);
      const label =
        name === endonym
          ? endonym
          : t("shared:languageOptionLabel", { name, endonym });
      return { label, value: option.value };
    }),
  ];
}

export function voiceCatalogueAccentOptionsForLanguage(
  language: string,
): readonly VoiceCatalogueFilterOption[] {
  return ELEVENLABS_ACCENT_OPTIONS_BY_LANGUAGE[language.trim()] ?? [];
}
